/**
 * T-447 — shared types + pure helpers for the tenant "My Bills" screen and the
 * dunning/suspension banners. Mirrors the backend `GET /subscription/bills` and
 * `GET /subscription/dunning-status` payloads (no codegen — keep in sync with
 * `backend/src/modules/subscription/billing-links.util.ts`).
 */

export interface PaymentContact {
  /** Prefilled wa.me link, or null when the founder has not configured a number. */
  whatsappUrl: string | null;
  /** Bank-transfer instructions, or null when none are configured. */
  bank: { title: string | null; iban: string | null; name: string | null } | null;
  supportEmail: string;
}

/** `GET /subscription/dunning-status` — payment fields only present for billing:read callers (T-452). */
export interface DunningStatus {
  overdue: boolean;
  suspended?: boolean;
  daysOverdue?: number;
  daysUntilSuspension?: number;
  stage?: number;
  dueDate?: string;
  supportEmail?: string;
  amount?: number;
  billId?: string;
  currency?: string;
  paymentContact?: PaymentContact;
}

export interface BillLineItem {
  description: string;
  amount: number;
}

export interface SelfBill {
  id: string;
  billNumber: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  planAmount: number;
  addonsAmount: number;
  totalAmount: number;
  currency: string;
  status: string;
  dueDate: string;
  paidAt: string | null;
  lineItems: unknown;
}

export interface SelfBillsResponse {
  bills: SelfBill[];
  paymentContact: PaymentContact;
}

/** Defensively narrow the `lineItems` JSON column to `{description, amount}[]`. */
export function parseLineItems(raw: unknown): BillLineItem[] {
  if (!Array.isArray(raw)) return [];
  const out: BillLineItem[] = [];
  for (const item of raw) {
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const amount = Number(rec.amount);
      if (typeof rec.description === 'string' && Number.isFinite(amount)) {
        out.push({ description: rec.description, amount });
      }
    }
  }
  return out;
}

/** True when the tenant has any way to pay other than the email fallback. */
export function hasPayChannel(contact: PaymentContact | undefined | null): boolean {
  return !!contact && (!!contact.whatsappUrl || !!contact.bank);
}
